import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import {
  adminGrowthSnapshotDeleteQuestionAction,
  adminGrowthSnapshotQuestionsListAction,
  adminGrowthSnapshotStepGetAction
} from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function typeLabel(type) {
  if (type === "multiple_choice") return "Multiple choice";
  if (type === "text") return "Text";
  return "Single choice";
}

export default function GrowthSnapshotQuestions() {
  const { snapshotId, sectionId, stepId } = useParams();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const stepsPath = `/growth-snapshots/${snapshotId}/sections/${sectionId}/steps`;

  useEffect(() => {
    let cancelled = false;
    async function loadMeta() {
      try {
        const res = await adminGrowthSnapshotStepGetAction(stepId);
        if (cancelled) return;
        if (res?.success) setStep(res.data || null);
      } catch (e) {}
    }
    if (stepId) loadMeta();
    return () => {
      cancelled = true;
    };
  }, [stepId]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim() ? query.trim() : undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminGrowthSnapshotQuestionsListAction(stepId, { search: q });
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.message || "Failed to load questions.");
        setRows(Array.isArray(res.data) ? res.data : []);
        if (res.step) setStep(res.step);
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setError(e?.response?.data?.message || e?.message || "Failed to load questions.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [stepId, query, reloadNonce]);

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage) || 1);
  const pageRows = useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return rows.slice(startIdx, startIdx + perPage);
  }, [rows, page, perPage]);

  const viewRows = useMemo(() => {
    return pageRows.map((r) => ({
      raw: r,
      id: r?.id,
      question: r?.question || "—",
      questionNumber: r?.question_number != null ? `Q${r.question_number}` : "—",
      questionGroup: r?.question_group === "looking_back" ? "Looking Back" : "Check-in",
      answerKey: r?.answer_key || "—",
      questionType: typeLabel(r?.question_type),
      category: r?.category_key || "—",
      scoring: r?.scoring_type || "none",
      optionsCount: Number(r?.options_count ?? 0),
      displayOrder: r?.display_order != null && r?.display_order !== "" ? String(r.display_order) : "0",
      statusLabel: Number(r?.status) === 1 ? "Active" : "Inactive",
      created: formatDate(r?.created_at)
    }));
  }, [pageRows]);

  const start = total && viewRows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && viewRows.length ? start + viewRows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const clearAndReload = () => {
    setQuery("");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminGrowthSnapshotDeleteQuestionAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || "Delete failed");
      toast.success("Question deleted", { toastId: "gs-question-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.message || "Delete failed", { toastId: "gs-question-delete" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Questions unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="breadcrumb">
        <Link to={`/growth-snapshots/${snapshotId}/sections`}>Sections</Link>
        <span>›</span>
        <Link to={stepsPath}>Steps</Link>
        <span>›</span>
        <span>{step?.title || "Step"}</span>
        <span>›</span>
        <span>Questions</span>
      </div>

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search questions..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>

        <div className="flex items-center gap-3">
          <Link to={stepsPath} className="btn btn-outline btn-sm">
            ← Steps
          </Link>
          <Link to="new" className="btn btn-primary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add question
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Question</th>
                <th>Group</th>
                <th>Type</th>
                <th>Category</th>
                <th>Scoring</th>
                <th>Options</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : (
                viewRows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.questionNumber}</td>
                    <td>
                      <strong>
                        {String(r.question).length > 80 ? `${String(r.question).slice(0, 80)}…` : r.question}
                      </strong>
                      {r.questionGroup === "Looking Back" && r.answerKey !== "—" ? (
                        <div className="small-label">{r.answerKey}</div>
                      ) : null}
                    </td>
                    <td>{r.questionGroup}</td>
                    <td>{r.questionType}</td>
                    <td>{r.category}</td>
                    <td>{r.scoring}</td>
                    <td>{r.optionsCount}</td>
                    <td>{r.displayOrder}</td>
                    <td>
                      <span className={`badge ${r.statusLabel === "Active" ? "badge-success" : "badge-gray"}`}>
                        {r.statusLabel}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`${r.id}/edit`} className="action-link">
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          onClick={() => openDelete(r.raw)}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Delete
                        </button>
                    
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!loading && viewRows.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <div className="empty-state">
                      <div className="empty-icon">🫥</div>
                      No questions yet.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            {total === 0
              ? "No questions"
              : `Showing ${start}–${end} of ${total} Question${total === 1 ? "" : "s"} · Page ${page} of ${totalPages}`}
          </span>
          {total > 0 ? (
            <div className="pagination-pages">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={page <= 1 || loading}
                title="First page"
                aria-label="First page"
              >
                {"‹‹"}
              </button>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                ‹
              </button>
              {pageButtons.map((p) => (
                <button
                  key={p}
                  className={p === page ? "active" : ""}
                  type="button"
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                ›
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
                title="Last page"
                aria-label="Last page"
              >
                {"››"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="question"
        itemName={deleteRow?.question || "Question"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}
